import { JSONError } from '@/bundle/Error/classes/JSONError';
import { RequiredAuthError } from '@/bundle/Error/classes/RequiredAuthError';
import { ResponseError } from '@/bundle/Error/classes/ResponseError';
import { ApiResponseType, RequestOptions } from '@/types/sharedTypes';
import { ApiParams, urlApiHelper } from './helpers/apiHelpers';
import { authTokenService, AuthTokensType } from '@/bundle/shared/services/authTokenService';
import {
  ACCESS_DENIED_RESPONSE,
  APPLICATION_CSV_CONTENT_TYPE,
  APPLICATION_JSON_CONTENT_TYPE,
  APPLICATION_MS_EXCEL_CONTENT_TYPE,
  APPLICATION_PDF_CONTENT_TYPE,
  BLOCKED_USER_RESPONSE,
  DEFAULT_LANGUAGE,
  EXPIRED_TOKEN_ERROR_TYPE,
  HTTP_FORBIDDEN_STATUS,
  HTTP_METHOD,
  HTTP_SUCCESS_STATUS,
  HTTP_UNAUTHORIZED_STATUS,
  INVALID_PASSWORD_RESPONSE,
  INVALID_TOKEN_RESPONSE,
} from './const';
import { BlockedUserError } from '@/bundle/Error/classes/BlockedUserError';
import { AccessDeniedError } from '@/bundle/Error/classes/AccessDeniedError';
import { TOKEN_REFRESHED_EVENT } from '@/const/shared';
import { HttpGetError } from '@/bundle/Error/classes/HttpGetError';

let refreshingTokenProcess = null;
const tokenRefreshedEvent = new CustomEvent(TOKEN_REFRESHED_EVENT);

const getContentType = (headers: Headers) => {
  return headers.get('content-type');
};

const buildOptions = async (headers: HeadersInit = {}, isUpload?: boolean) => {
  const tokens = authTokenService.getTokens();

  const options: RequestInit = {
    headers: {
      ...(isUpload ? {} : { 'Content-Type': APPLICATION_JSON_CONTENT_TYPE }),
      'Accept-Language': DEFAULT_LANGUAGE,
      authorization: `Bearer ${tokens?.access}`,
      ...headers,
    },
    credentials: 'include',
  };

  return options;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const contentType = getContentType(response.headers);
  let content;

  try {
    if (contentType === APPLICATION_JSON_CONTENT_TYPE) {
      content = await response.json();
    } else if (contentType === APPLICATION_PDF_CONTENT_TYPE || contentType === APPLICATION_CSV_CONTENT_TYPE) {
      content = await response.blob();
    } else if (contentType === null) {
      content = {
        ok: true,
      };
    } else {
      content = response;
    }

    return content;
  } catch (error) {
    throw new ResponseError();
  }
};

export const authApiService = {
  async GET<T>(uri: string, params?: ApiParams, requestOptions?: RequestOptions): Promise<ApiResponseType<T>> {
    return await authApiService.makeRequest<T>(HTTP_METHOD.GET, uri, null, params, requestOptions);
  },

  async POST<T>(
    uri: string,
    data: unknown,
    params?: ApiParams,
    requestOptions?: RequestOptions
  ): Promise<ApiResponseType<T>> {
    return await authApiService.makeRequest<T>(HTTP_METHOD.POST, uri, data, params, requestOptions);
  },

  async PATCH<T>(
    uri: string,
    data: unknown,
    params?: ApiParams,
    requestOptions?: RequestOptions
  ): Promise<ApiResponseType<T>> {
    return await authApiService.makeRequest<T>(HTTP_METHOD.PATCH, uri, data, params, requestOptions);
  },

  async PUT<T>(uri: string, data: unknown, params?: ApiParams): Promise<ApiResponseType<T>> {
    return await authApiService.makeRequest<T>(HTTP_METHOD.PUT, uri, data, params);
  },

  async DELETE<T>(uri: string, data?: ApiParams): Promise<ApiResponseType<T>> {
    return await authApiService.makeRequest<T>(HTTP_METHOD.DELETE, uri, data);
  },

  async GET_XLS<T>(uri: string, params?: ApiParams): Promise<ApiResponseType<T>> {
    const url = urlApiHelper(uri, params);
    const options = await buildOptions({
      'Content-Type': APPLICATION_MS_EXCEL_CONTENT_TYPE,
    });
    const response = await fetch(url, options);
    const data = await authApiService.handleResponse(response, null, {
      method: HTTP_METHOD.GET,
      uri,
      params,
      payload: null,
    });

    if (data.response?.status !== HTTP_SUCCESS_STATUS) {
      throw new Error('Download XLS error. Try again later');
    }

    const blobData = await response.blob();

    return {
      response,
      json: blobData as T,
    };
  },

  async UPLOAD_FILE<T>(
    uri: string,
    payload: BodyInit,
    params: ApiParams = null,
    requestOptions = {} as RequestOptions
  ) {
    const url = urlApiHelper(uri, params);
    const { headers, ...restOptions } = await buildOptions(null, true);

    const response = await fetch(url, {
      method: HTTP_METHOD.POST,
      headers: { ...headers, ...requestOptions.requestHeaders },
      ...(payload && { body: payload }),
      ...restOptions,
    });

    const parsedData = await parseResponse<T>(response);

    return await authApiService.handleResponse(
      response,
      parsedData,
      { method: HTTP_METHOD.POST, uri, payload, params },
      requestOptions
    );
  },

  async makeRequest<T>(
    method: string,
    uri: string,
    payload: unknown,
    params: ApiParams = null,
    requestOptions = {} as RequestOptions
  ): Promise<ApiResponseType<T>> {
    const url = urlApiHelper(uri, params);
    const { headers, ...restOptions } = await buildOptions();
    const body = method === HTTP_METHOD.GET ? null : JSON.stringify(payload);

    const response = await fetch(url, {
      method,
      headers: { ...headers, ...requestOptions.requestHeaders },
      ...(body && { body }),
      ...restOptions,
    });

    const parsedData = await parseResponse<T>(response);

    return await authApiService.handleResponse(response, parsedData, { method, uri, payload, params }, requestOptions);
  },

  async handleResponse<T>(
    response: Response,
    json: T,
    { method, uri, payload, params },
    requestOptions?: RequestOptions
  ): Promise<ApiResponseType<T>> {
    // @ts-expect-error: field <error_type> would be only exists in response when token is expired
    const isTokenExpired = (json as unknown)?.error_type === EXPIRED_TOKEN_ERROR_TYPE;
    // @ts-expect-error: field <error_message> would be only exists in response when token is expired
    const errorMessage = (json as unknown)?.error_message as string;
    const { status } = response;

    if (status === HTTP_UNAUTHORIZED_STATUS && isTokenExpired) {
      return await authApiService.refreshToken(method, uri, payload, params, requestOptions);
    }

    if (status === HTTP_UNAUTHORIZED_STATUS && errorMessage === INVALID_PASSWORD_RESPONSE) {
      return { response, json };
    }

    if (status === HTTP_UNAUTHORIZED_STATUS) {
      throw new RequiredAuthError();
    }

    if (status === HTTP_FORBIDDEN_STATUS && errorMessage === ACCESS_DENIED_RESPONSE) {
      throw new AccessDeniedError();
    }

    if (status === HTTP_FORBIDDEN_STATUS && errorMessage === BLOCKED_USER_RESPONSE) {
      throw new BlockedUserError();
    }

    if (method === HTTP_METHOD.GET && !response.ok) {
      throw new HttpGetError(errorMessage, status);
    }

    return { response, json };
  },

  async renewToken(tokens: AuthTokensType) {
    const url = urlApiHelper('/users/refresh-tokens/');
    const body = { refresh: tokens?.refresh };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': APPLICATION_JSON_CONTENT_TYPE,
      },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const contentType = getContentType(response.headers);

    if (contentType !== APPLICATION_JSON_CONTENT_TYPE) {
      refreshingTokenProcess = null;

      throw new JSONError();
    }

    const json = await response.json();

    if (json === INVALID_TOKEN_RESPONSE) {
      refreshingTokenProcess = null;

      throw new RequiredAuthError(json);
    }

    if (response?.ok) {
      const newTokens = { ...tokens, ...json };

      authTokenService.setTokens(newTokens);
      refreshingTokenProcess = null;

      window.dispatchEvent(tokenRefreshedEvent);

      return;
    }

    refreshingTokenProcess = null;

    throw new RequiredAuthError(json);
  },

  async refreshToken<T>(originalMethod, uri, payload, params, requestOptions?: RequestOptions) {
    const tokens = authTokenService.getTokens();

    if (!tokens?.user_id || !tokens?.refresh) {
      throw new RequiredAuthError();
    }

    if (!refreshingTokenProcess) {
      refreshingTokenProcess = authApiService.renewToken(tokens);
    }

    await refreshingTokenProcess;

    const originalRequestResponse = await authApiService.makeRequest(
      originalMethod,
      uri,
      payload,
      params,
      requestOptions
    );

    return originalRequestResponse as T;
  },
};
